import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { AppBar, Button, Hidden, Tab, Tabs, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import SwipeableViews from 'react-swipeable-views';
import CopyToClipboard from 'react-copy-to-clipboard';
import clsx from 'clsx';

import { openModal, setFooter } from 'features/utilsSlice';
import { getRequest } from 'utils/api';
import { previewUrl } from 'utils/helpers';
import Toast from 'utils/toast';

import useProfileOwner from 'hooks/useProfileOwner';
import AddIcon from 'public/icons/add-white.svg';
import ShareIcon from 'public/icons/share-dark.svg';
import InfluencerNetworks from './InfluencerNetworks';
import ProfilePreviewDialog from './ProfilePreviewDialog';
import ProfileCover from 'components/profile/ProfileCover';
import ProfileInfo from 'components/profile/ProfileInfo';
import WishCard from 'components/wishlist/WishCard';
import WishFormDialog from 'components/wishlist/WishFormDialog';
import WishOrderCard from 'components/wishlist/WishOrderCard';
import WishCheckoutDialog from 'components/wishlist/WishCheckoutDialog';
import IconMainContainer from 'components/IconMainContainer';
import SuccessPaymentDialog from 'components/wishlist/SuccessPaymentDialog';
import styles from 'styles/components/profile/Profile.module.scss';

const removeDuplicates = array =>
  array.filter((el, index) => index === array.findIndex(item => item.id === el.id));

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#f8f9fb',
    borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
  },

  textColorPrimary: {
    textTransform: 'capitalize',
    fontFamily: 'Avenir Roman',
    fontSize: 15,
    overflow: 'unset !important',

    [theme.breakpoints.up('sm')]: {
      minWidth: 120,
    },
  },

  colorPrimary: {
    borderBottom: 0,
    backgroundColor: '#f8f9fb',
  },

  color: {
    borderBottom: 0,
    backgroundColor: '#f8f9fb',
  },
}));

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <div>{children}</div>}
  </div>
);

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const FanProfile = ({ profile, setProfile }) => {
  const [activeWishes, setActiveWishes] = useState([]);
  const [completedWishes, setCompletedWishes] = useState([]);
  const [isLoading, setIsLoading] = useState({});
  const [url, setUrl] = useState('');
  const [value, setValue] = useState(0);
  const isLoggedIn = useSelector(state => state.auth.loggedIn);
  const dispatch = useDispatch();
  const router = useRouter();
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { wish } = router.query;
  const { tab } = router.query;

  const profileOwner = useProfileOwner(profile);

  const padding = { padding: isMobile ? '20px 15px' : '20px 0 20px 5px' };

  const inventories = profileOwner
    ? profile.inventories
    : profile.inventories.filter(
        inventory => inventory.active && !inventory.inventory_items.every(item => !item.active)
      );

  const influencerNetworks = inventories.map(inventory => inventory.influencer_network);

  const uniqInfluencerNetworks = removeDuplicates(influencerNetworks);

  const handleChange = (_event, newValue) => setValue(newValue);

  const handleChangeIndex = index => setValue(index);

  useEffect(() => {
    setIsLoading(prevState => ({ ...prevState, activeWishes: true, completedWishes: true }));

    getRequest({ endpoint: `wishlist_items`, query: { profile_id: profile.id, active: true } })
      .then(wishlist => {
        setActiveWishes(wishlist);
      })
      .catch(() => {})
      .finally(() =>
        setIsLoading(prevState => ({ ...prevState, activeWishes: false, completedWishes: false }))
      );
  }, [profile.id]);

  useEffect(() => {
    setIsLoading(prevState => ({ ...prevState, completedWishes: true }));

    getRequest({ endpoint: 'profiles/wish_orders' })
      .then(orders => {
        setCompletedWishes(orders);
        setIsLoading(prevState => ({ ...prevState, completedWishes: false }));
      })
      .catch(() => setIsLoading(prevState => ({ ...prevState, completedWishes: false })));
  }, []);

  useEffect(() => {
    dispatch(setFooter(isLoggedIn ? true : false));
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (!wish) return;

    const wishListItem = activeWishes.find(item => item.id === +wish);

    if (wishListItem) {
      dispatch(openModal({ name: 'Wish Checkout', data: { wish: wishListItem } }));
    }
  }, [dispatch, wish, activeWishes]);

  useEffect(() => {
    if (!tab) return;

    if (tab === 'orders') setValue(1);
  }, [tab]);

  useEffect(() => {
    const currentUrl = previewUrl(`/u/${profile.username}`);

    setUrl(`${currentUrl}/wishlist`);
  }, [profile.username]);

  return (
    <div
      className={clsx(styles.profileContainer, {
        [styles.loggedIn]: isLoggedIn,
        [styles.notLoggedIn]: !isLoggedIn,
      })}
      style={!isLoggedIn ? { paddingBottom: 0 } : {}}
    >
      {!isLoggedIn && profile.adult ? (
        <div className={styles.modelCover} />
      ) : (
        <ProfileCover setProfile={setProfile} profile={profile} />
      )}

      <ProfileInfo
        setProfile={setProfile}
        profile={profile}
        uniqInfluencerNetworks={uniqInfluencerNetworks}
      />

      {uniqInfluencerNetworks.length > 0 && (
        <Hidden mdUp>
          <InfluencerNetworks profile={profile} influencerNetworks={uniqInfluencerNetworks} />
        </Hidden>
      )}

      <h2 className={clsx(styles.wishTitle, { [styles.notLoggedInMargin]: !isLoggedIn })}>
        WELCOME TO MY WISH LIST!
      </h2>

      <div className={styles.wishContainer}>
        {profileOwner && (
          <div className={styles.buttons}>
            <Button
              onClick={() => dispatch(openModal({ name: 'Wish Form' }))}
              color="primary"
              variant="contained"
              endIcon={<AddIcon />}
              fullWidth
            >
              Add a wish
            </Button>

            <CopyToClipboard text={url}>
              <Button
                variant="outlined"
                color="secondary"
                endIcon={<ShareIcon />}
                style={{ height: 40, flex: 1 }}
                fullWidth
                onClick={() => {
                  Toast('success', 'Copied wishlist page link!', {
                    icon: (
                      <IconMainContainer src="/icons/notifications/thumb-up.svg" alt="Thumb Up" />
                    ),
                  });
                }}
              >
                Share
              </Button>
            </CopyToClipboard>
          </div>
        )}

        {profileOwner && (
          <AppBar position="static" className={classes.colorPrimary}>
            <Tabs
              value={value}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              className={classes.root}
            >
              <Tab label="Active wishes" className={classes.textColorPrimary} disableRipple />
              <Tab label="Wish contributions" className={classes.textColorPrimary} disableRipple />
            </Tabs>
          </AppBar>
        )}

        <SwipeableViews
          disabled={!profileOwner}
          axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
          index={value}
          onChangeIndex={handleChangeIndex}
          className={value === 0 && classes.color}
          slideStyle={{ overflow: 'unset' }}
          containerStyle={{
            transition: 'transform 0.35s cubic-bezier(0.15, 0.3, 0.25, 1) 0s',
          }}
        >
          <TabPanel value={value} index={0} style={padding}>
            {activeWishes.length === 0 && !isLoading.activeWishes && (
              <Alert severity="info">No active wishes</Alert>
            )}

            {activeWishes.length > 0 && (
              <div className={styles.wishContent}>
                {activeWishes
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map(wish => (
                    <WishCard key={wish.id} wish={wish} profile={profile} />
                  ))}
              </div>
            )}
          </TabPanel>

          <TabPanel value={value} index={1} style={{ padding: 15 }}>
            {completedWishes.length === 0 && !isLoading.completedWishes && (
              <Alert severity="info">No completed wishes</Alert>
            )}

            {completedWishes.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                {completedWishes
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .map(order => (
                    <WishOrderCard key={order.id} order={order} userKey="buyer" />
                  ))}
              </div>
            )}
          </TabPanel>
        </SwipeableViews>
      </div>

      <ProfilePreviewDialog profile={profile} setProfile={setProfile} />
      <WishFormDialog setActiveWishes={setActiveWishes} />
      <WishCheckoutDialog profile={profile} />
      <SuccessPaymentDialog />
    </div>
  );
};

FanProfile.propTypes = {
  setProfile: PropTypes.func.isRequired,
  profile: PropTypes.object,
};

FanProfile.defaultProps = {
  profile: {},
};

export default FanProfile;
